/**
 * B2C Lead Radar — multi-module workspace dedicated to B2C social listening.
 *
 * Layout: left vertical tabs (11 modules) · main content panel.
 *
 * Modules:
 *   1. Visão Geral        — KPI cards + chart-grid (leads/source/day/intent…)
 *   2. Fontes Monitoradas — full source catalog with compliance + cost
 *   3. Palavras-chave     — intent library reuse
 *   4. Leads Detectados   — list view (Kanban placeholder)
 *   5. Alertas            — Hot Alerts config status
 *   6. Concorrentes       — full CRUD
 *   7. B2C Intent Score   — score histogram + criteria breakdown
 *   8. Filtro Anti-Ruído  — negative keywords + per-mention explanation
 *   9. Respostas com IA   — recent AI replies + 3-style preview
 *  10. Relatórios         — CSV export + top-of-week
 *  11. Configurações      — settings summary
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Radar, Activity, Globe, Tag, ListChecks, BellRing, Crosshair,
  Gauge, ShieldAlert, Bot, FileBarChart, Settings, Loader2,
  RefreshCw, Plus, Trash2, ExternalLink, Download, AlertTriangle,
  Check, XCircle, TrendingUp, TrendingDown, MessageSquare, Eye,
} from "lucide-react";
import Sidebar from "../../components/Sidebar";
import Logo from "../../components/Logo";
import LangToggle from "../../components/LangToggle";
import ThemeToggle from "../../components/ThemeToggle";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtInt = (v) => new Intl.NumberFormat("pt-BR").format(Math.round(v || 0));
const fmtPct = (v) => v == null ? "—" : `${v.toFixed(1)}%`;

const MODULES = [
  { key: "overview",     label: "Visão Geral",         icon: Activity },
  { key: "sources",      label: "Fontes Monitoradas",  icon: Globe },
  { key: "keywords",     label: "Palavras-chave",      icon: Tag },
  { key: "leads",        label: "Leads Detectados",    icon: ListChecks },
  { key: "alerts",       label: "Alertas",             icon: BellRing },
  { key: "competitors",  label: "Concorrentes",        icon: Crosshair },
  { key: "score",        label: "Intent Score",        icon: Gauge },
  { key: "antinoise",    label: "Filtro Anti-Ruído",   icon: ShieldAlert },
  { key: "ai_replies",   label: "Respostas com IA",    icon: Bot },
  { key: "reports",      label: "Relatórios",          icon: FileBarChart },
  { key: "settings",     label: "Configurações",       icon: Settings },
];

export default function B2CLeadRadar() {
  // Sync the active tab with URL ?tab= so deep-links + refresh keep state.
  const initial = (() => {
    if (typeof window === "undefined") return "overview";
    const sp = new URLSearchParams(window.location.search);
    const t = sp.get("tab");
    return MODULES.find(m => m.key === t)?.key || "overview";
  })();
  const [active, setActive] = useState(initial);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const url = new URL(window.location.href);
    url.searchParams.set("tab", active);
    window.history.replaceState({}, "", url);
  }, [active]);

  return (
    <div className="min-h-screen bg-[var(--al-bg)] text-[var(--al-text)]"
         data-testid="b2c-lead-radar-page">
      <Sidebar />
      <div className="ml-[var(--al-sidebar-w,260px)] flex flex-col min-w-0">
        <header className="h-14 border-b border-[var(--al-border)] flex items-center justify-between px-6 sticky top-0 z-20 bg-[var(--al-bg)]">
          <div className="flex items-center gap-3">
            <Logo />
            <span className="hidden sm:inline-flex items-center gap-1.5 text-[12px] text-[var(--al-text-muted)] font-mono-tech">
              <Radar className="w-3.5 h-3.5 text-[#C9A24E]" /> B2C · Lead Radar
            </span>
          </div>
          <div className="flex items-center gap-3">
            <ThemeToggle />
            <LangToggle />
          </div>
        </header>

        <div className="flex flex-1 min-h-0">
          {/* Left tabs */}
          <aside className="w-[228px] shrink-0 border-r border-[var(--al-border)] py-4 px-2 overflow-y-auto"
                 data-testid="b2c-radar-tabs">
            <div className="px-3 mb-3">
              <h1 className="text-[18px] font-extrabold leading-tight flex items-center gap-2">
                <Radar className="w-4 h-4 text-[#C9A24E]" /> B2C Lead Radar
              </h1>
              <p className="text-[11px] text-[var(--al-text-muted)] mt-1 leading-snug">
                Encontre consumidores demonstrando intenção de compra em conversas públicas.
              </p>
            </div>
            <nav className="space-y-0.5">
              {MODULES.map((m, i) => (
                <button key={m.key} onClick={() => setActive(m.key)}
                  data-testid={`b2c-radar-tab-${m.key}`}
                  className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-[12.5px] transition-colors ${
                    active === m.key
                      ? "bg-[#C9A24E]/15 text-[var(--al-text)] font-bold border border-[#C9A24E]/40"
                      : "text-[var(--al-text-muted)] hover:bg-[var(--al-card-bg)]"
                  }`}>
                  <span className="w-5 text-[10px] font-mono-tech text-[var(--al-text-muted)]">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <m.icon className="w-3.5 h-3.5" />
                  <span className="text-left flex-1">{m.label}</span>
                </button>
              ))}
            </nav>
            <ComplianceNotice />
          </aside>

          <main className="flex-1 p-6 overflow-y-auto" data-testid="b2c-radar-main">
            {active === "overview"     && <ModuleOverview />}
            {active === "sources"      && <ModuleSources />}
            {active === "keywords"     && <ModuleKeywords />}
            {active === "leads"        && <ModuleLeads />}
            {active === "alerts"       && <ModuleAlerts />}
            {active === "competitors"  && <ModuleCompetitors />}
            {active === "score"        && <ModuleScore />}
            {active === "antinoise"    && <ModuleAntiNoise />}
            {active === "ai_replies"   && <ModuleAIReplies />}
            {active === "reports"      && <ModuleReports />}
            {active === "settings"     && <ModuleSettings />}
          </main>
        </div>
      </div>
    </div>
  );
}

function ComplianceNotice() {
  return (
    <div className="mt-5 mx-2 p-3 rounded-lg border border-amber-400/40 bg-amber-400/10 text-amber-700 dark:text-amber-300"
         data-testid="b2c-radar-compliance">
      <div className="flex items-start gap-2">
        <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
        <p className="text-[10.5px] leading-snug">
          Use apenas para monitorar conversas <b>públicas ou autorizadas</b>.
          Responda de forma ética e contextual. Nunca envie spam nem colete dados sensíveis.
        </p>
      </div>
    </div>
  );
}

/* ============================================================
   MODULE 1 — Visão Geral
   ============================================================ */
function ModuleOverview() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/b2c-radar/overview`, { params: { days } });
      setData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally { setLoading(false); }
  }, [days]);

  useEffect(() => { load(); }, [load]);

  if (loading && !data) return <Loading label="Carregando overview…" />;
  if (!data) return null;

  const cards = [
    { label: "Leads hoje", v: data.cards.today, accent: "#3B82F6" },
    { label: "Alta intenção (≥70)", v: data.cards.high_intent, accent: "#10B981" },
    { label: "Pedidos de recomendação", v: data.cards.recommendation_requests, accent: "#A855F7" },
    { label: "Reclamações de concorrente", v: data.cards.competitor_complaints, accent: "#EF4444" },
    { label: "Respostas IA geradas", v: data.cards.replies_generated, accent: "#F59E0B" },
    { label: "Respondidos", v: data.cards.replied, accent: "#06B6D4" },
    { label: "Convertidos", v: data.cards.converted, accent: "#10B981" },
    { label: "Total no período", v: data.cards.total_in_window, accent: "#64748B" },
    { label: "Taxa qualificada", v: fmtPct(data.cards.qualified_rate_pct), accent: "#C9A24E", isStr: true },
  ];

  return (
    <div data-testid="b2c-radar-overview">
      <ModuleHeader title="Visão Geral" subtitle="KPIs + sentimento + top performers em uma única tela.">
        <select value={days} onChange={e => setDays(Number(e.target.value))}
          data-testid="overview-days"
          className="bg-[var(--al-card-bg)] border border-[var(--al-border)] rounded-lg px-3 h-9 text-[12px]">
          <option value={7}>últimos 7 dias</option>
          <option value={14}>últimos 14 dias</option>
          <option value={30}>últimos 30 dias</option>
          <option value={60}>últimos 60 dias</option>
          <option value={90}>últimos 90 dias</option>
        </select>
        <button onClick={load} disabled={loading}
          className="h-9 px-3 rounded-lg border border-[var(--al-border)] text-[12px] inline-flex items-center gap-1.5 hover:bg-[var(--al-card-bg)]">
          {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          Atualizar
        </button>
      </ModuleHeader>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 mb-6"
           data-testid="overview-cards">
        {cards.map(c => (
          <div key={c.label} className="rounded-2xl border bg-[var(--al-card-bg)] p-4"
               style={{ borderColor: "var(--al-border)" }}>
            <div className="text-[10.5px] uppercase tracking-wider text-[var(--al-text-muted)] font-bold">{c.label}</div>
            <div className="text-[22px] font-extrabold mt-1 leading-none" style={{ color: c.accent }}>
              {c.isStr ? c.v : fmtInt(c.v)}
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <BarChartCard title="Leads por fonte" data={data.by_source} testid="chart-source" />
        <BarChartCard title="Leads por intenção" data={data.by_intent} testid="chart-intent" />
        <BarChartCard title="Top palavras-chave" data={data.top_keywords} testid="chart-keywords" />
        <BarChartCard title="Top concorrentes mencionados" data={data.top_competitors} testid="chart-competitors" />
        <BarChartCard title="Sentimento das conversas" data={data.by_sentiment} testid="chart-sentiment" colors={{ positive: "#10B981", neutral: "#94A3B8", negative: "#EF4444" }} />
        <BarChartCard title="Distribuição de status" data={data.status_distribution} testid="chart-status" />
        <BarChartCard title="Histograma do Intent Score" data={data.score_histogram.map(x => ({ k: x.bucket, n: x.n }))} testid="chart-score-hist" />
        <SparkLineCard title="Leads por dia" series={data.daily} testid="chart-daily" />
      </div>
    </div>
  );
}

function BarChartCard({ title, data, testid, colors = {} }) {
  const max = Math.max(1, ...data.map(d => d.n));
  return (
    <div className="rounded-2xl border bg-[var(--al-card-bg)] p-4"
         style={{ borderColor: "var(--al-border)" }} data-testid={testid}>
      <h3 className="text-[12px] font-bold uppercase tracking-wider mb-3 text-[var(--al-text-muted)]">{title}</h3>
      {data.length === 0 ? (
        <p className="text-[12px] text-[var(--al-text-muted)] py-6 text-center">Sem dados ainda.</p>
      ) : (
        <div className="space-y-1.5">
          {data.slice(0, 8).map((d, i) => (
            <div key={`${d.k}-${i}`} className="flex items-center gap-2 text-[11.5px]">
              <span className="truncate w-32 shrink-0 font-mono-tech">{d.k || "—"}</span>
              <div className="flex-1 bg-[var(--al-border)]/40 rounded h-2 overflow-hidden">
                <div className="h-full rounded transition-all"
                     style={{ width: `${(d.n / max) * 100}%`,
                              background: colors[d.k] || "#C9A24E" }} />
              </div>
              <span className="font-bold w-8 text-right">{fmtInt(d.n)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function SparkLineCard({ title, series, testid }) {
  const max = Math.max(1, ...series.map(d => d.n));
  const W = 600, H = 80;
  const path = series.map((d, i) => {
    const x = series.length === 1 ? W / 2 : (i / (series.length - 1)) * W;
    const y = H - (d.n / max) * H;
    return `${i === 0 ? "M" : "L"} ${x.toFixed(1)} ${y.toFixed(1)}`;
  }).join(" ");
  return (
    <div className="rounded-2xl border bg-[var(--al-card-bg)] p-4"
         style={{ borderColor: "var(--al-border)" }} data-testid={testid}>
      <h3 className="text-[12px] font-bold uppercase tracking-wider mb-3 text-[var(--al-text-muted)]">{title}</h3>
      {series.length === 0 ? (
        <p className="text-[12px] text-[var(--al-text-muted)] py-6 text-center">Sem dados.</p>
      ) : (
        <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-20">
          <path d={path} fill="none" stroke="#C9A24E" strokeWidth="2" />
        </svg>
      )}
      <div className="text-[10.5px] text-[var(--al-text-muted)] flex justify-between mt-2">
        <span>{series[0]?.day || ""}</span>
        <span>{series.at(-1)?.day || ""}</span>
      </div>
    </div>
  );
}

/* ============================================================
   MODULE 2 — Fontes
   ============================================================ */
function ModuleSources() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/b2c-radar/sources-catalog`);
        setItems(data.sources || []);
      } catch (e) { toast.error(e.message); }
      finally { setLoading(false); }
    })();
  }, []);

  if (loading) return <Loading />;

  return (
    <div data-testid="b2c-radar-sources">
      <ModuleHeader title="Fontes Monitoradas" subtitle="Catálogo de fontes públicas + APIs autorizadas. Ative apenas o que você usa." />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {items.map(s => (
          <div key={s.key}
            data-testid={`source-${s.key}`}
            className="rounded-2xl border bg-[var(--al-card-bg)] p-4"
            style={{ borderColor: "var(--al-border)" }}>
            <div className="flex items-start justify-between mb-2">
              <div>
                <h3 className="font-bold text-[14px]">{s.name}</h3>
                <p className="text-[11px] text-[var(--al-text-muted)]">{s.type}</p>
              </div>
              <span className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded ${
                s.is_active
                  ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300"
                  : "bg-slate-500/15 text-slate-500"
              }`}>
                {s.is_active ? "Ativa" : "Inativa"}
              </span>
            </div>
            <Meta k="Método" v={s.method} />
            <Meta k="Frequência" v={s.frequency} />
            <Meta k="Custo" v={s.cost} />
            <Meta k="Limitações" v={s.limitations} />
            <Meta k="Compliance" v={s.compliance} />
            {s.signup_url && (
              <a href={s.signup_url} target="_blank" rel="noreferrer"
                 className="mt-2 inline-flex items-center gap-1 text-[11.5px] text-[#C9A24E] hover:underline">
                Configurar / Documentação <ExternalLink className="w-3 h-3" />
              </a>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function Meta({ k, v }) {
  if (!v) return null;
  return (
    <div className="flex gap-2 text-[11px] py-0.5 leading-snug">
      <span className="font-bold w-20 shrink-0 text-[var(--al-text-muted)]">{k}:</span>
      <span>{v}</span>
    </div>
  );
}

/* ============================================================
   MODULE 3 — Keywords (lightweight reuse of existing endpoints)
   ============================================================ */
function ModuleKeywords() {
  const [lib, setLib] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/b2c-intent/keyword-library`);
        setLib(data);
      } catch (e) { toast.error(e.message); }
      finally { setLoading(false); }
    })();
  }, []);

  if (loading) return <Loading />;
  if (!lib) return null;
  const cats = Object.keys(lib.library || {});

  return (
    <div data-testid="b2c-radar-keywords">
      <ModuleHeader title="Palavras-chave de Intenção"
        subtitle="Biblioteca PT + EN + ES classificada por tipo de intenção. Edição completa em B2C Intent → Keyword Library." />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {cats.map(cat => (
          <div key={cat} className="rounded-2xl border bg-[var(--al-card-bg)] p-4"
               style={{ borderColor: "var(--al-border)" }}
               data-testid={`kw-cat-${cat}`}>
            <h3 className="text-[13px] font-bold mb-2 uppercase tracking-wide text-[#C9A24E]">{cat}</h3>
            <div className="flex flex-wrap gap-1.5">
              {(lib.library[cat] || []).slice(0, 24).map(k => (
                <span key={k} className="text-[11px] px-2 py-0.5 rounded bg-[var(--al-border)]/30 font-mono-tech">
                  {k}
                </span>
              ))}
              {(lib.library[cat] || []).length > 24 && (
                <span className="text-[11px] px-2 py-0.5 text-[var(--al-text-muted)]">
                  +{lib.library[cat].length - 24}
                </span>
              )}
            </div>
          </div>
        ))}
      </div>
      <a href="/b2c-intent" className="mt-4 inline-flex items-center gap-1.5 text-[12px] font-bold text-[#C9A24E] hover:underline">
        Editar biblioteca completa <ExternalLink className="w-3 h-3" />
      </a>
    </div>
  );
}

/* ============================================================
   MODULE 4 — Leads Detectados (table view)
   ============================================================ */
function ModuleLeads() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [minScore, setMinScore] = useState(40);
  const [source, setSource] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/b2c-intent/mentions`,
        { params: { min_score: minScore, source, limit: 200 } });
      setItems(data.items || []);
    } catch (e) { toast.error(e.message); }
    finally { setLoading(false); }
  }, [minScore, source]);

  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="b2c-radar-leads">
      <ModuleHeader title="Leads Detectados"
        subtitle={`${items.length} menções com score ≥ ${minScore}.`}>
        <select value={minScore} onChange={e => setMinScore(Number(e.target.value))}
          data-testid="leads-min-score"
          className="bg-[var(--al-card-bg)] border border-[var(--al-border)] rounded-lg px-3 h-9 text-[12px]">
          <option value={0}>Todos os scores</option>
          <option value={40}>Score ≥ 40</option>
          <option value={70}>Score ≥ 70 (Alta)</option>
        </select>
        <select value={source} onChange={e => setSource(e.target.value)}
          data-testid="leads-source"
          className="bg-[var(--al-card-bg)] border border-[var(--al-border)] rounded-lg px-3 h-9 text-[12px]">
          <option value="">Todas as fontes</option>
          <option value="reddit">Reddit</option>
          <option value="hackernews">Hacker News</option>
          <option value="rss">RSS</option>
        </select>
        <button onClick={load} disabled={loading}
          className="h-9 px-3 rounded-lg border border-[var(--al-border)] text-[12px] inline-flex items-center gap-1.5 hover:bg-[var(--al-card-bg)]">
          {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          Atualizar
        </button>
      </ModuleHeader>

      {loading ? <Loading /> : items.length === 0 ? (
        <EmptyState title="Nenhuma menção ainda"
          desc="Vá em B2C Intent e rode uma busca para popular o radar." />
      ) : (
        <div className="rounded-2xl border bg-[var(--al-card-bg)] overflow-hidden"
             style={{ borderColor: "var(--al-border)" }}>
          <table className="w-full text-[12px]">
            <thead className="text-[10px] uppercase tracking-wider text-[var(--al-text-muted)]">
              <tr>
                <th className="px-3 py-2 text-left">Score</th>
                <th className="px-3 py-2 text-left">Título</th>
                <th className="px-3 py-2 text-left">Fonte</th>
                <th className="px-3 py-2 text-left">Intenção</th>
                <th className="px-3 py-2 text-left">Sent.</th>
                <th className="px-3 py-2 text-left">Detectado</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {items.map(m => (
                <tr key={m.id} className="border-t border-[var(--al-border)]"
                    data-testid={`lead-row-${m.id}`}>
                  <td className="px-3 py-2"><ScoreBadge score={m.intent_score || 0} /></td>
                  <td className="px-3 py-2 max-w-md">
                    <div className="font-bold truncate">{m.title || "(sem título)"}</div>
                    <div className="text-[10.5px] text-[var(--al-text-muted)] line-clamp-1">{m.snippet}</div>
                  </td>
                  <td className="px-3 py-2 font-mono-tech text-[11px]">{m.source}</td>
                  <td className="px-3 py-2 text-[11px]">{m.intent_category || "—"}</td>
                  <td className="px-3 py-2 text-[11px]">
                    <SentimentChip s={m.sentiment} />
                  </td>
                  <td className="px-3 py-2 text-[10.5px] text-[var(--al-text-muted)]">
                    {(m.detected_at || "").slice(0, 16).replace("T", " ")}
                  </td>
                  <td className="px-3 py-2">
                    {m.url && (
                      <a href={m.url} target="_blank" rel="noreferrer"
                         className="inline-flex items-center gap-1 text-[#C9A24E] hover:underline">
                        <Eye className="w-3 h-3" /> abrir
                      </a>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function ScoreBadge({ score }) {
  const color = score >= 70 ? "#10B981" : score >= 40 ? "#F59E0B" : "#94A3B8";
  return (
    <span className="px-2 py-0.5 rounded font-mono-tech font-bold text-[11px]"
          style={{ background: `${color}20`, color }}>
      {score}
    </span>
  );
}

function SentimentChip({ s }) {
  const map = { positive: { c: "#10B981", l: "+" },
                negative: { c: "#EF4444", l: "-" },
                neutral:  { c: "#94A3B8", l: "·" } };
  const cfg = map[s] || map.neutral;
  return (
    <span style={{ color: cfg.c }} className="font-bold">
      {cfg.l} {s || "neutral"}
    </span>
  );
}

/* ============================================================
   MODULE 5 — Alertas
   ============================================================ */
function ModuleAlerts() {
  const [cfg, setCfg] = useState(null);
  const [log, setLog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [slackUrl, setSlackUrl] = useState("");
  const [slackHint, setSlackHint] = useState("");
  const [slackEnabled, setSlackEnabled] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, l] = await Promise.all([
        axios.get(`${API}/alerts/config`),
        axios.get(`${API}/alerts/log`),
      ]);
      setCfg(c.data); setLog(l.data?.items || []);
      setSlackEnabled(!!c.data?.slack?.enabled);
      setSlackHint(c.data?.slack?.channel_hint || "");
      // Don't pre-fill the URL (it's masked); user re-enters when changing.
    } catch (e) { toast.error(e.message); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveSlack = async () => {
    setSaving(true);
    try {
      const payload = {
        threshold: cfg?.threshold ?? 85,
        slack: {
          enabled: slackEnabled,
          webhook_url: slackUrl || (cfg?.slack?.webhook_url_masked && !slackUrl ? "" : slackUrl),
          channel_hint: slackHint,
        },
      };
      // If user typed nothing in URL but is just toggling enabled, they
      // need to also include current persisted webhook. Server preserves
      // when payload omits fields? No — Pydantic resets unknown fields.
      // Simplest UX: require a URL when enabling.
      if (slackEnabled && !slackUrl && !cfg?.slack?.webhook_url_masked) {
        toast.error("Cole a URL do Webhook Slack antes de ativar");
        setSaving(false); return;
      }
      const { data } = await axios.post(`${API}/alerts/config`, payload);
      setCfg(data);
      toast.success("Configuração Slack salva");
      setSlackUrl("");
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    finally { setSaving(false); }
  };

  const testSlack = async () => {
    setTesting(true);
    try {
      const { data } = await axios.post(`${API}/alerts/test?channel=slack`);
      if (data?.result?.ok) toast.success("Teste Slack enviado!");
      else toast.error("Falha: " + (data?.result?.error || "verifique a URL"));
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    finally { setTesting(false); }
  };

  if (loading) return <Loading />;

  return (
    <div data-testid="b2c-radar-alerts">
      <ModuleHeader title="Alertas em Tempo Real"
        subtitle="Disparo automático quando uma menção pública atinge o threshold definido." />

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-3 mb-5">
        <Tile label="Status" value={cfg?.configured ? "ATIVO" : "INATIVO"}
              accent={cfg?.configured ? "#10B981" : "#94A3B8"} />
        <Tile label="Score mínimo" value={cfg?.threshold ?? 85} accent="#C9A24E" />
        <Tile label="E-mail" value={cfg?.telegram?.enabled || cfg?.twilio?.enabled ? "ON" : "OFF"} accent="#3B82F6" />
        <Tile label="Slack" value={cfg?.slack?.enabled ? "ON" : "OFF"} accent={cfg?.slack?.enabled ? "#10B981" : "#94A3B8"} />
      </div>

      {/* Slack webhook config card */}
      <div className="rounded-2xl border bg-[var(--al-card-bg)] p-5 mb-4"
           style={{ borderColor: "var(--al-border)" }}
           data-testid="alerts-slack-card">
        <div className="flex items-start justify-between mb-3">
          <div>
            <h3 className="font-bold text-[14px] flex items-center gap-2">
              <BellRing className="w-4 h-4 text-emerald-500" /> Slack — webhook nativo
            </h3>
            <p className="text-[12px] text-[var(--al-text-muted)] mt-1 max-w-xl">
              Crie um <a className="underline" href="https://api.slack.com/messaging/webhooks" target="_blank" rel="noreferrer">Incoming Webhook</a> no seu workspace Slack e cole a URL aqui.
              As menções com score ≥ {cfg?.threshold ?? 85} serão postadas como cards com Block Kit.
            </p>
          </div>
          <label className="inline-flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={slackEnabled}
              data-testid="slack-enabled-toggle"
              onChange={(e) => setSlackEnabled(e.target.checked)}
              className="accent-emerald-500" />
            <span className="text-[12px] font-bold">Habilitado</span>
          </label>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Input label="Webhook URL"
            v={slackUrl}
            onChange={setSlackUrl}
            testid="slack-webhook-input" />
          <Input label="Canal (referência humana)"
            v={slackHint}
            onChange={setSlackHint}
            testid="slack-hint-input" />
        </div>
        {cfg?.slack?.webhook_url_masked && (
          <p className="text-[10.5px] text-[var(--al-text-muted)] mt-2">
            URL atual: <code className="font-mono-tech">{cfg.slack.webhook_url_masked}</code>
            {!slackUrl && " (deixe vazio para manter)"}
          </p>
        )}
        <div className="flex gap-2 mt-3">
          <button onClick={saveSlack} disabled={saving}
            data-testid="slack-save-btn"
            className="h-9 px-4 rounded-lg bg-[#C9A24E] text-black text-[12px] font-bold inline-flex items-center gap-1.5">
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
            Salvar
          </button>
          <button onClick={testSlack} disabled={testing || !cfg?.slack?.enabled}
            data-testid="slack-test-btn"
            title={!cfg?.slack?.enabled ? "Salve com 'Habilitado' primeiro" : ""}
            className="h-9 px-4 rounded-lg border border-[var(--al-border)] text-[12px] font-bold inline-flex items-center gap-1.5 hover:bg-[var(--al-card-bg)] disabled:opacity-40">
            {testing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <BellRing className="w-3.5 h-3.5" />}
            Enviar teste
          </button>
        </div>
      </div>

      <div className="rounded-2xl border bg-[var(--al-card-bg)] p-4"
           style={{ borderColor: "var(--al-border)" }}>
        <h3 className="font-bold text-[13px] mb-3">Histórico de alertas</h3>
        {log.length === 0 ? (
          <EmptyState title="Sem alertas disparados"
            desc="Configure o canal acima e ative em B2C Intent → Hot Alerts." />
        ) : (
          <ul className="space-y-2 text-[12px]">
            {log.slice(0, 20).map((row, i) => (
              <li key={i} className="border-b border-[var(--al-border)] pb-2 last:border-0">
                <div className="font-bold">{row.title || row.subject || "(sem título)"}</div>
                <div className="text-[10.5px] text-[var(--al-text-muted)]">
                  {row.sent_at || row.created_at} · canais: {(row.channels || []).join(", ") || "—"}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
      <p className="mt-3 text-[11.5px] text-[var(--al-text-muted)]">
        Canais suportados: <b>Slack</b> (webhook), Telegram, Twilio SMS. WhatsApp em breve.
      </p>
    </div>
  );
}

/* ============================================================
   MODULE 6 — Concorrentes
   ============================================================ */
function ModuleCompetitors() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ name: "", url: "", products: "", niche: "" });
  const [running, setRunning] = useState(null);  // competitor id currently watching

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/b2c-radar/competitors`);
      setItems(data.items || []);
    } catch (e) { toast.error(e.message); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!form.name.trim()) { toast.error("Nome obrigatório"); return; }
    try {
      await axios.post(`${API}/b2c-radar/competitors`, form);
      setForm({ name: "", url: "", products: "", niche: "" });
      setAdding(false);
      load();
      toast.success("Concorrente adicionado");
    } catch (e) { toast.error(e.message); }
  };
  const remove = async (id) => {
    if (!window.confirm("Remover concorrente?")) return;
    try {
      await axios.delete(`${API}/b2c-radar/competitors/${id}`);
      load();
    } catch (e) { toast.error(e.message); }
  };
  const toggleWatch = async (c) => {
    try {
      await axios.patch(`${API}/b2c-radar/competitors/${c.id}`,
        { watch_active: !c.watch_active });
      load();
    } catch (e) { toast.error(e.message); }
  };
  const runWatch = async (c) => {
    setRunning(c.id);
    try {
      const { data } = await axios.post(`${API}/b2c-radar/competitors/${c.id}/watch/run`);
      if (data.ok) {
        toast.success(`Watch executado: ${data.inserted} novas menções (${data.fetched} fetched)`);
        load();
      } else {
        toast.error(data.error || "falha");
      }
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    finally { setRunning(null); }
  };
  const runAll = async () => {
    setRunning("__all__");
    try {
      const { data } = await axios.post(`${API}/b2c-radar/competitors/watch/run-all`);
      const total = (data.results || []).reduce((acc, r) => acc + (r.inserted || 0), 0);
      toast.success(`Watch global executado em ${data.ran} concorrentes · +${total} menções`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    finally { setRunning(null); }
  };

  const activeWatches = items.filter(c => c.watch_active).length;

  return (
    <div data-testid="b2c-radar-competitors">
      <ModuleHeader title="Concorrentes Monitorados"
        subtitle={`${items.length} cadastrados · ${activeWatches} com Watch ativo (varredura diária Reddit + Google Alerts).`}>
        <button onClick={runAll}
          disabled={running === "__all__" || activeWatches === 0}
          data-testid="competitors-watch-runall"
          className="h-9 px-3 rounded-lg border border-emerald-500/40 text-emerald-600 dark:text-emerald-400 text-[12px] font-bold inline-flex items-center gap-1.5 hover:bg-emerald-500/10 disabled:opacity-40">
          {running === "__all__"
            ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
            : <RefreshCw className="w-3.5 h-3.5" />}
          Rodar todos os Watches
        </button>
        <button onClick={() => setAdding(true)}
          data-testid="competitor-add-btn"
          className="h-9 px-3 rounded-lg bg-[#C9A24E] text-black text-[12px] font-bold inline-flex items-center gap-1.5">
          <Plus className="w-3.5 h-3.5" /> Adicionar
        </button>
      </ModuleHeader>

      {/* Competitor Watch info banner */}
      <div className="mb-4 rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-3 flex gap-3">
        <Crosshair className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" />
        <p className="text-[12px] text-[var(--al-text-muted)]">
          <b className="text-emerald-600 dark:text-emerald-400">Competitor Watch</b> — ative o toggle 🛰 em qualquer concorrente para que o radar busque <b>diariamente</b> em Reddit + Google Alerts RSS por termos como "alternativa para X", "X cancelar", "X ruim", "replace X". Funciona como early-warning de churn — você fecha o lead antes do seu concorrente notar a insatisfação.
        </p>
      </div>

      {adding && (
        <div className="rounded-2xl border bg-[var(--al-card-bg)] p-4 mb-4"
             style={{ borderColor: "var(--al-border)" }} data-testid="competitor-form">
          <h3 className="font-bold mb-3 text-[13px]">Novo concorrente</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Input label="Nome" v={form.name} onChange={(v) => setForm({...form, name: v})} testid="comp-name" />
            <Input label="URL" v={form.url} onChange={(v) => setForm({...form, url: v})} testid="comp-url" />
            <Input label="Produtos/serviços" v={form.products} onChange={(v) => setForm({...form, products: v})} testid="comp-products" />
            <Input label="Nicho" v={form.niche} onChange={(v) => setForm({...form, niche: v})} testid="comp-niche" />
          </div>
          <div className="flex gap-2 mt-3">
            <button onClick={add} data-testid="comp-save"
              className="h-9 px-4 rounded-lg bg-[#C9A24E] text-black text-[12px] font-bold">
              Salvar
            </button>
            <button onClick={() => setAdding(false)}
              className="h-9 px-4 rounded-lg border border-[var(--al-border)] text-[12px]">
              Cancelar
            </button>
          </div>
        </div>
      )}

      {loading ? <Loading /> : items.length === 0 ? (
        <EmptyState title="Nenhum concorrente cadastrado"
          desc="Adicione concorrentes pra monitorar reclamações e comparações públicas." />
      ) : (
        <div className="space-y-2">
          {items.map(c => (
            <div key={c.id}
              data-testid={`competitor-row-${c.id}`}
              className="rounded-xl border bg-[var(--al-card-bg)] p-3 flex items-center justify-between gap-3"
              style={{ borderColor: c.watch_active ? "rgba(16,185,129,0.4)" : "var(--al-border)" }}>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-bold text-[13px] truncate">{c.name}</span>
                  {c.watch_active && (
                    <span className="text-[9px] uppercase font-bold px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-700 dark:text-emerald-300">
                      🛰 Watch ON
                    </span>
                  )}
                </div>
                <div className="text-[11px] text-[var(--al-text-muted)] truncate">
                  {c.products || "—"} {c.niche ? `· ${c.niche}` : ""}
                  {c.url ? <> · <a href={c.url} target="_blank" rel="noreferrer" className="text-[#C9A24E]">{c.url}</a></> : null}
                </div>
                {c.last_watch_at && (
                  <div className="text-[10.5px] text-[var(--al-text-muted)] mt-1">
                    Último watch: {c.last_watch_at.slice(0, 16).replace("T", " ")}
                    {c.last_watch_inserted != null && ` · +${c.last_watch_inserted} menções`}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <button onClick={() => toggleWatch(c)}
                  data-testid={`competitor-watch-toggle-${c.id}`}
                  title={c.watch_active ? "Desativar Watch" : "Ativar Watch (varredura diária)"}
                  className={`px-2 py-1.5 rounded text-[11px] font-bold border ${
                    c.watch_active
                      ? "bg-emerald-500/20 text-emerald-700 dark:text-emerald-300 border-emerald-500/40"
                      : "border-[var(--al-border)] text-[var(--al-text-muted)] hover:bg-[var(--al-bg)]"
                  }`}>
                  🛰 {c.watch_active ? "ON" : "OFF"}
                </button>
                <button onClick={() => runWatch(c)}
                  disabled={running === c.id}
                  data-testid={`competitor-watch-run-${c.id}`}
                  title="Rodar watch agora"
                  className="px-2 py-1.5 rounded text-[11px] font-bold border border-[var(--al-border)] hover:bg-[var(--al-bg)] disabled:opacity-40">
                  {running === c.id
                    ? <Loader2 className="w-3 h-3 animate-spin" />
                    : <RefreshCw className="w-3 h-3" />}
                </button>
                <button onClick={() => remove(c.id)}
                  data-testid={`competitor-del-${c.id}`}
                  className="text-red-500 hover:bg-red-500/10 p-2 rounded">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Input({ label, v, onChange, testid }) {
  return (
    <label className="block">
      <span className="text-[11px] font-bold uppercase tracking-wider text-[var(--al-text-muted)]">{label}</span>
      <input value={v} onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="mt-1 w-full bg-[var(--al-bg)] border border-[var(--al-border)] rounded-lg px-3 h-9 text-[12.5px] focus:border-[#C9A24E] outline-none" />
    </label>
  );
}

/* ============================================================
   MODULE 7 — Intent Score
   ============================================================ */
function ModuleScore() {
  const criteria = [
    { l: "Palavra-chave de alta intenção", v: "+30", c: "#10B981" },
    { l: "Pedido explícito de recomendação", v: "+25", c: "#10B981" },
    { l: "Reclamação contra concorrente", v: "+25", c: "#10B981" },
    { l: "Intenção direta de compra", v: "+30", c: "#10B981" },
    { l: "Pedido de orçamento ou preço", v: "+25", c: "#10B981" },
    { l: "Menção recente (<24h)", v: "+15", c: "#10B981" },
    { l: "Sentimento negativo + concorrente", v: "+20", c: "#10B981" },
    { l: "Sentimento positivo sobre necessidade", v: "+10", c: "#10B981" },
    { l: "Fonte qualificada (Reddit nicho)", v: "+10", c: "#10B981" },
    { l: "Texto claro e específico", v: "+10", c: "#10B981" },
    { l: "Texto genérico ou sem contexto", v: "-10", c: "#EF4444" },
    { l: "Conteúdo antigo (>30d)", v: "-10", c: "#EF4444" },
    { l: "Ruído ou baixa relevância", v: "-15", c: "#EF4444" },
    { l: "Sem intenção comercial clara", v: "-20", c: "#EF4444" },
  ];
  const tiers = [
    { from: 0, to: 39, label: "Baixa oportunidade", color: "#94A3B8" },
    { from: 40, to: 69, label: "Média oportunidade", color: "#F59E0B" },
    { from: 70, to: 100, label: "Alta oportunidade", color: "#10B981" },
  ];
  return (
    <div data-testid="b2c-radar-score">
      <ModuleHeader title="B2C Intent Score"
        subtitle="Pontuação 0-100 por menção. Critérios totalmente transparentes." />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 mb-5">
        {tiers.map(t => (
          <div key={t.label} className="rounded-2xl border bg-[var(--al-card-bg)] p-4"
               style={{ borderColor: "var(--al-border)" }}>
            <div className="text-[10.5px] font-bold uppercase tracking-wider text-[var(--al-text-muted)]">
              {t.from}–{t.to}
            </div>
            <div className="text-[20px] font-extrabold mt-1" style={{ color: t.color }}>{t.label}</div>
          </div>
        ))}
      </div>
      <div className="rounded-2xl border bg-[var(--al-card-bg)] p-4"
           style={{ borderColor: "var(--al-border)" }}>
        <h3 className="font-bold mb-3 text-[13px]">Critérios de pontuação</h3>
        <ul className="grid grid-cols-1 md:grid-cols-2 gap-1.5 text-[12.5px]">
          {criteria.map(c => (
            <li key={c.l} className="flex items-center justify-between border-b border-[var(--al-border)] py-1">
              <span>{c.l}</span>
              <span className="font-mono-tech font-bold" style={{ color: c.c }}>{c.v}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

/* ============================================================
   MODULE 8 — Anti-Ruído
   ============================================================ */
function ModuleAntiNoise() {
  const layers = [
    "Filtro por palavra-chave",
    "Filtro por contexto da conversa",
    "Filtro por intenção comercial",
    "Filtro por relevância do nicho",
    "Filtro por termos negativos",
    "Filtro por idioma",
    "Filtro por localização (quando disponível)",
    "Filtro por duplicidade (URL única)",
    "Filtro por conteúdo antigo (>30 dias)",
    "Filtro por baixa clareza textual",
  ];
  const negatives = [
    "vaga", "estágio", "curso grátis", "apostila", "pdf", "download",
    "meme", "piada", "notícia antiga", "acadêmico", "trabalho escolar",
    "concurso", "faculdade", "tcc",
  ];
  return (
    <div data-testid="b2c-radar-antinoise">
      <ModuleHeader title="Filtro Anti-Ruído"
        subtitle="10 camadas de filtragem inspiradas no HotLeadAlerts. A plataforma explica POR QUÊ um lead foi mantido ou descartado." />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <div className="rounded-2xl border bg-[var(--al-card-bg)] p-4"
             style={{ borderColor: "var(--al-border)" }}>
          <h3 className="font-bold mb-3 text-[13px]">Camadas ativas</h3>
          <ul className="space-y-1.5 text-[12.5px]">
            {layers.map((l, i) => (
              <li key={l} className="flex items-center gap-2">
                <Check className="w-3.5 h-3.5 text-emerald-500" />
                <span className="font-mono-tech text-[11px] text-[var(--al-text-muted)]">{String(i+1).padStart(2,"0")}</span>
                {l}
              </li>
            ))}
          </ul>
        </div>
        <div className="rounded-2xl border bg-[var(--al-card-bg)] p-4"
             style={{ borderColor: "var(--al-border)" }}>
          <h3 className="font-bold mb-3 text-[13px]">Termos negativos padrão</h3>
          <div className="flex flex-wrap gap-1.5">
            {negatives.map(n => (
              <span key={n} className="text-[11px] px-2 py-0.5 rounded bg-red-500/10 text-red-600 dark:text-red-400 font-mono-tech">
                {n}
              </span>
            ))}
          </div>
          <p className="mt-3 text-[11.5px] text-[var(--al-text-muted)]">
            Personalize em <a className="underline" href="/b2c-intent#filter">B2C Intent → Negative Keywords</a>.
          </p>
        </div>
      </div>
    </div>
  );
}

/* ============================================================
   MODULE 9 — Respostas com IA
   ============================================================ */
function ModuleAIReplies() {
  const styles = [
    { k: "consultive", l: "Consultiva",
      d: "Diagnostica o problema real, contextualiza, oferece ajuda como passo opcional." },
    { k: "direct", l: "Direta",
      d: "Aponta a solução prática direto. Menciona a marca apenas se relevante." },
    { k: "educational", l: "Educativa",
      d: "Ensina o tema em 2-3 parágrafos, sugere a marca como uma das opções." },
  ];
  return (
    <div data-testid="b2c-radar-ai-replies">
      <ModuleHeader title="Respostas com IA"
        subtitle="3 estilos. Sempre revisão humana antes de publicar. Nunca automatize publicação." />
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-5">
        {styles.map(s => (
          <div key={s.k} className="rounded-2xl border bg-[var(--al-card-bg)] p-4"
               style={{ borderColor: "var(--al-border)" }}
               data-testid={`reply-style-${s.k}`}>
            <div className="text-[11px] font-bold uppercase tracking-wider text-[#C9A24E]">{s.l}</div>
            <p className="text-[12.5px] mt-2 text-[var(--al-text-muted)]">{s.d}</p>
          </div>
        ))}
      </div>
      <div className="rounded-2xl border border-amber-500/40 bg-amber-500/10 p-4 flex gap-3">
        <AlertTriangle className="w-5 h-5 text-amber-500 shrink-0" />
        <div className="text-[12.5px]">
          <b>Regra obrigatória:</b> a resposta gerada pela IA NUNCA é publicada automaticamente.
          Toda resposta passa pela revisão humana via Inbox antes de ser usada.
          Para gerar uma resposta para uma menção específica, abra-a em Leads Detectados → "Gerar resposta com IA".
        </div>
      </div>
    </div>
  );
}

/* ============================================================
   MODULE 10 — Relatórios
   ============================================================ */
function ModuleReports() {
  const downloadCsv = async () => {
    try {
      const url = `${API}/b2c-radar/reports/leads.csv`;
      const a = document.createElement("a");
      a.href = url; a.download = "b2c-radar-leads.csv";
      document.body.appendChild(a); a.click(); a.remove();
      toast.success("Download iniciado");
    } catch (e) { toast.error(e.message); }
  };
  return (
    <div data-testid="b2c-radar-reports">
      <ModuleHeader title="Relatórios"
        subtitle="Exporte os leads detectados pra CSV ou planilha. PDF e Sheets em breve." />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="rounded-2xl border bg-[var(--al-card-bg)] p-5"
             style={{ borderColor: "var(--al-border)" }}>
          <h3 className="font-bold text-[14px] mb-2">Leads detectados (CSV)</h3>
          <p className="text-[12.5px] text-[var(--al-text-muted)] mb-4">
            Inclui: id, fonte, título, URL, palavra-chave, intenção, sentimento, score, concorrente.
          </p>
          <button onClick={downloadCsv}
            data-testid="reports-csv-btn"
            className="h-10 px-4 rounded-lg bg-[#C9A24E] text-black text-[12.5px] font-bold inline-flex items-center gap-2">
            <Download className="w-4 h-4" /> Baixar CSV
          </button>
        </div>
        <div className="rounded-2xl border bg-[var(--al-card-bg)] p-5"
             style={{ borderColor: "var(--al-border)" }}>
          <h3 className="font-bold text-[14px] mb-2">Em breve</h3>
          <ul className="text-[12.5px] space-y-1 text-[var(--al-text-muted)]">
            <li>· Google Sheets export</li>
            <li>· Sync para CRM</li>
            <li>· Webhook por evento</li>
            <li>· PDF executive report (semanal)</li>
          </ul>
        </div>
      </div>
    </div>
  );
}

/* ============================================================
   MODULE 11 — Configurações
   ============================================================ */
function ModuleSettings() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/b2c-radar/settings`);
        setData(data);
      } catch (e) { toast.error(e.message); }
      finally { setLoading(false); }
    })();
  }, []);

  if (loading) return <Loading />;

  return (
    <div data-testid="b2c-radar-settings">
      <ModuleHeader title="Configurações"
        subtitle="Resumo do projeto atual + atalhos para ajuste fino." />
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
        <Tile label="Palavras-chave" value={data?.counts?.keywords} accent="#C9A24E" />
        <Tile label="Concorrentes" value={data?.counts?.competitors} accent="#A855F7" />
        <Tile label="Menções armazenadas" value={data?.counts?.mentions} accent="#3B82F6" />
        <Tile label="Alertas configurados" value={data?.counts?.alerts_config} accent="#10B981" />
      </div>
      <div className="rounded-2xl border bg-[var(--al-card-bg)] p-5"
           style={{ borderColor: "var(--al-border)" }}>
        <h3 className="font-bold text-[13px] mb-3">Projeto padrão</h3>
        {data?.default_project ? (
          <div className="space-y-1.5 text-[12.5px]">
            <Meta k="Nome" v={data.default_project.project_name} />
            <Meta k="Marca" v={data.default_project.brand_name} />
            <Meta k="Nicho" v={data.default_project.niche} />
            <Meta k="País" v={data.default_project.country} />
            <Meta k="Idioma" v={data.default_project.language} />
          </div>
        ) : (
          <EmptyState title="Nenhum projeto criado"
            desc="Crie um projeto em B2C Intent → Projects pra começar." />
        )}
      </div>
      <p className="mt-4 text-[11.5px] text-[var(--al-text-muted)]">
        Ajustes avançados (palavras negativas, idioma, frequência, score mínimo) ficam em <a className="underline" href="/b2c-intent">B2C Intent</a>.
      </p>
    </div>
  );
}

/* ============================================================
   Shared mini-components
   ============================================================ */
function ModuleHeader({ title, subtitle, children }) {
  return (
    <div className="mb-5 flex flex-wrap gap-3 items-end justify-between">
      <div>
        <h2 className="text-[20px] font-extrabold leading-tight">{title}</h2>
        {subtitle && <p className="text-[12.5px] text-[var(--al-text-muted)] mt-1 max-w-2xl">{subtitle}</p>}
      </div>
      <div className="flex gap-2">{children}</div>
    </div>
  );
}

function Tile({ label, value, accent }) {
  return (
    <div className="rounded-2xl border bg-[var(--al-card-bg)] p-4"
         style={{ borderColor: "var(--al-border)" }}>
      <div className="text-[10.5px] uppercase tracking-wider text-[var(--al-text-muted)] font-bold">{label}</div>
      <div className="text-[24px] font-extrabold mt-1 leading-none" style={{ color: accent }}>
        {typeof value === "number" ? fmtInt(value) : (value ?? "—")}
      </div>
    </div>
  );
}

function Loading({ label = "Carregando…" }) {
  return (
    <div className="py-16 text-center text-[var(--al-text-muted)] inline-flex items-center justify-center gap-2 w-full text-[13px]">
      <Loader2 className="w-4 h-4 animate-spin" /> {label}
    </div>
  );
}

function EmptyState({ title, desc }) {
  return (
    <div className="py-12 text-center">
      <div className="font-bold text-[14px]">{title}</div>
      <div className="text-[12px] text-[var(--al-text-muted)] mt-1">{desc}</div>
    </div>
  );
}
